import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import { formatMoney } from "@/lib/currencyFormat";
import type { DashboardFinancial } from "@fiscalo/shared-types";

interface DashboardMonthSummaryProps {
  financial: DashboardFinancial;
}

/**
 * This-month headline for the simple view — a port of the mobile Inicio
 * screen's `MonthSummaryCard`.
 *
 * Shows one number: neto = ventas − compras, both already UYU-equivalent
 * (foreign currencies are converted server-side). Collapsed by default; the
 * ventas / compras breakdown appears on tap, so the card opens with a single
 * figure rather than the four stat cells `DashboardFinancialSummary` leads
 * with in the advanced view.
 */
export function DashboardMonthSummary({
  financial,
}: DashboardMonthSummaryProps): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // Both fields are optional on DashboardFinancial — a backend that hasn't
  // computed them yet must read as 0, not NaN.
  const ventas = financial.totalInvoicedThisMonthUyuEquivalent ?? 0;
  const compras = financial.totalRecibidasThisMonthUyu ?? 0;

  // Same guard as mobile: a tenant with no movement this month gets a clean
  // page rather than a card reading "$U 0,00".
  if (ventas <= 0 && compras <= 0) return null;

  const net = ventas - compras;
  const monthLabel = new Intl.DateTimeFormat(i18n.language, { month: "short" })
    .format(new Date())
    .replace(/\./g, "")
    .toUpperCase();

  return (
    <div className="fx-hero-surface fx-month-hero">
      <button
        type="button"
        className="fx-month-hero-head"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <span className="fx-month-hero-eyebrow">
          {t("dashboard.monthSummary.eyebrow", "Resumen")} · {monthLabel}
        </span>

        <span className="fx-month-hero-main">
          <span className="fx-month-hero-net">
            <span className="fx-month-hero-amount">
              <span className="cur">$U</span>
              <span className="val">{formatMoney(net)}</span>
            </span>
            <span className="fx-month-hero-caption">
              {t(
                "dashboard.monthSummary.caption",
                "Neto del mes · ventas − compras",
              )}
            </span>
          </span>
          <ChevronRight
            className={`fx-month-hero-chev h-6 w-6${expanded ? " is-open" : ""}`}
            aria-hidden
          />
        </span>
      </button>

      {expanded ? (
        <div className="fx-month-hero-detail">
          <SummaryRow
            label={t("dashboard.monthSummary.ventas", "Ventas")}
            amount={ventas}
            to="/invoices?period=month"
          />
          <SummaryRow
            label={t("dashboard.monthSummary.compras", "Compras")}
            amount={compras}
            to="/invoices?segment=received"
          />
        </div>
      ) : null}
    </div>
  );
}

interface SummaryRowProps {
  label: string;
  amount: number;
  to: string;
}

function SummaryRow({ label, amount, to }: SummaryRowProps): JSX.Element {
  return (
    <Link to={to} className="fx-month-hero-row">
      <span className="fx-month-hero-row-label">{label}</span>
      <span className="fx-month-hero-row-amount">
        <span className="cur">$U</span>
        <span className="val">{formatMoney(amount)}</span>
      </span>
    </Link>
  );
}
